// ════════════════════════════════════════════════════════════════════════════
//  Consulta de ajustes de estoque (Cycle Counting + Transferência)
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, Search } from 'lucide-react';
import { cycleService as svc } from '@/features/wms/wmsService';
import { LocalEstoqueSelect } from '@/components/LocalEstoqueSelect';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { useDebounce } from '@/hooks/useDebounce';
import type { TamanhoPagina } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MOTIVOS = ['Todos', 'Contagem', 'Rejeitado', 'Transferencia'];
const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

export default function AjustesEstoque() {
  const [local, setLocal] = useState<number | undefined>();
  const [motivo, setMotivo] = useState('Todos');
  const [produtoId, setProdutoId] = useState<number | undefined>();

  // Paginado no servidor (antes: os 500 mais recentes, sem paginação nem período).
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [busca, setBusca] = useState('');
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'data' | 'ajuste'>('data', true, () => setPagina(1));
  const filtros = {
    busca: buscaEnviada, produtoId, ...periodo.intervalo,
    ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
  };
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['ajustes-estoque', 'paginado', pagina, tamanhoPagina, local, motivo, filtros],
    queryFn: () => svc.listarAjustesPaginado(pagina, tamanhoPagina, local ?? 0, motivo === 'Todos' ? undefined : motivo, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });
  const ajustes = data?.itens ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, data?.totalPaginas ?? 1);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Consulta de Ajustes de Estoque</h1>
        <p className="text-muted-foreground mt-1">Auditoria de ajustes por contagem cíclica e divergências de transferência.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Ajustes</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-56"><Label>Local</Label>
              <LocalEstoqueSelect value={local} onChange={(id) => { setLocal(id); setPagina(1); }} rotuloTodos="Todos os locais" /></div>
            <div className="w-72"><Label>Produto</Label>
              <div className="flex gap-1">
                <ProdutoSearchCombobox value={produtoId} onChange={(id) => { setProdutoId(id || undefined); setPagina(1); }} placeholder="Todos os produtos" />
                {produtoId && <Button variant="ghost" size="sm" onClick={() => { setProdutoId(undefined); setPagina(1); }}>Limpar</Button>}
              </div></div>
            <div className="w-56"><Label>Motivo</Label>
              <Select value={motivo} onValueChange={(v) => { setMotivo(v); setPagina(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MOTIVOS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="w-full sm:w-64"><Label>Busca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Descrição ou código..." value={busca} className="pl-9"
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
              </div></div>
            <div><Label>Período</Label>
              <div className="flex flex-col sm:flex-row gap-2"><PeriodoFiltro estado={periodo} rotuloData="Data da contagem" /></div></div>
          </div>

          <Table>
            <TableHeader><TableRow>
              <CabecalhoOrdenavel coluna="data" ordenacao={ordenacao}>Data</CabecalhoOrdenavel><TableHead>Local</TableHead><TableHead>Produto</TableHead>
              <TableHead>Motivo</TableHead><TableHead className="text-right">Anterior</TableHead>
              <TableHead className="text-right">Contado</TableHead>
              <CabecalhoOrdenavel coluna="ajuste" ordenacao={ordenacao} className="text-right">Ajuste</CabecalhoOrdenavel><TableHead>Origem</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : !ajustes.length ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {periodo.periodo !== 'todos'
                    ? 'Nenhum ajuste neste período — amplie o período para ver os anteriores'
                    : 'Nenhum ajuste'}
                </TableCell></TableRow>
              ) : ajustes.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{new Date(a.dtContagem).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>{a.localEstoque ?? '—'}</TableCell>
                  <TableCell>{a.produto ?? '—'}</TableCell>
                  <TableCell><Badge variant="secondary">{a.motivo}</Badge></TableCell>
                  <TableCell className="text-right">{fmt(a.saldoAnterior)}</TableCell>
                  <TableCell className="text-right">{fmt(a.saldoContado)}</TableCell>
                  <TableCell className={`text-right ${a.ajuste < 0 ? 'text-destructive' : a.ajuste > 0 ? 'text-green-600' : ''}`}>{fmt(a.ajuste)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {a.contagemOrigemId ? `Contagem nº ${a.contagemOrigemNumero ?? '—'}` : a.transferenciaOrigemId ? `Transferência nº ${a.transferenciaOrigemNumero ?? '—'}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginacaoRodape
            pagina={pagina}
            totalPaginas={totalPaginas}
            totalItens={total}
            primeiroItem={total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1}
            ultimoItem={Math.min(pagina * tamanhoPagina, total)}
            tamanhoPagina={tamanhoPagina}
            onPaginaChange={setPagina}
            onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
          />
          {isFetching && !isLoading && <p className="text-xs text-muted-foreground">(atualizando...)</p>}
        </CardContent>
      </Card>
    </div>
  );
}
